import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Upload, X, ImageIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNotify } from "@/hooks/useNotify";

interface SupplierLogoUploadProps {
  value?: string | null;
  onChange: (url: string | null) => void;
  companyId: string;
  supplierId?: string;
}

const MAX_SIZE = 5 * 1024 * 1024;
const ACCEPTED = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];

export default function SupplierLogoUpload({
  value,
  onChange,
  companyId,
  supplierId,
}: SupplierLogoUploadProps) {
  const notify = useNotify();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    if (!ACCEPTED.includes(file.type)) {
      notify.error("Logo must be PNG, JPEG, WebP, or SVG.");
      return;
    }
    if (file.size > MAX_SIZE) {
      notify.error("Logo must be 5 MB or smaller.");
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "png";
      const path = `${companyId}/${supplierId ?? "new"}/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("supplier-logos")
        .upload(path, file, { upsert: false, cacheControl: "3600", contentType: file.type });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from("supplier-logos").getPublicUrl(path);
      onChange(data.publicUrl);
      notify.success("Logo uploaded");
    } catch (err) {
      notify.error(err);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-md border bg-muted/30 overflow-hidden">
        {value ? (
          <img src={value} alt="Supplier logo" className="h-full w-full object-contain" />
        ) : (
          <ImageIcon className="h-6 w-6 text-muted-foreground" />
        )}
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? "Uploading..." : value ? "Replace" : "Upload logo"}
          </Button>
          {value ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange(null)}
              disabled={uploading}
            >
              <X className="h-4 w-4 mr-1" />
              Remove
            </Button>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground">PNG, JPEG, WebP or SVG · up to 5 MB</p>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED.join(",")}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
          }}
        />
      </div>
    </div>
  );
}
